import { useDebounceCallback } from '@react-hook/debounce';
import {
  getProviderMapBySensorId,
  mapPersonalAccessTokenErrorReason,
  validationStatusToMessageMap
} from 'common/constants/constants';
import { get, trim } from 'lodash';
import { useEffect, useState } from 'react';
import { TokenValidationResult } from 'types';
import axios from 'common/requester';
import { API } from 'common/constants/api-urls';

interface UseTokenValidatorHandlerProps {
  sensorId: number;
  isCloudProvider: boolean;
}

const useTokenValidatorHandler = ({
  sensorId,
  isCloudProvider = true
}: UseTokenValidatorHandlerProps) => {
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const sensorMap: any = getProviderMapBySensorId(sensorId);

  const [successPayload, setSuccessPayload] =
    useState<TokenValidationResult | null>(null);
  const [failurePayload, setFailurePayload] = useState<any | null>(null);

  const isValid = status === validationStatusToMessageMap.VALID;

  const validateToken = useDebounceCallback(async () => {
    setLoading(true);

    const validationUrl = `${API.TEST_API_TOKEN_PATH}/${sensorMap.sensor_name}`;
    const body = {
      ...(!isCloudProvider ? { self_hosted_uri: sensorMap.baseUrl } : {}),
      access_token: token
    };

    try {
      const response = (await axios.post<TokenValidationResult>(
        validationUrl,
        body
      )) as TokenValidationResult;

      const { token_status, reason } = response;

      setStatus(token_status);

      if (token_status === validationStatusToMessageMap.VALID) {
        setLoading(false);
        setSuccessPayload(response);
        return;
      }

      const errReason = get(mapPersonalAccessTokenErrorReason, reason, '');

      const errorPayload = {
        message: `${validationStatusToMessageMap[token_status]} ${errReason}`,
        ...response
      };

      setFailurePayload(errorPayload);
      setLoading(false);
    } catch (error) {
      const errorPayload = {
        message: 'Failed to validate token. Please try again.',
        error
      };

      setFailurePayload(errorPayload);
      setLoading(false);
    }
  }, 1000);

  useEffect(() => {
    setToken(trim(token));
    setSuccessPayload(null);
    setFailurePayload(null);
    setStatus(null);

    if (token.length > 0) {
      validateToken();
    }
  }, [token]);

  return {
    token,
    setToken,
    status,
    loading,
    isValid,
    successPayload,
    failurePayload
  };
};

export default useTokenValidatorHandler;
